import { Building2, CalendarDays, Gavel, Layers3, Users } from "lucide-react";
import { Link } from "react-router";
import { formatDate, formatTenderAmount } from "~/lib/format";
import {
	type CompanyTender,
	sourceRecordCountLabel,
	TENDER_ROLE_LABELS,
	TENDER_SOURCE_LABELS,
	type TenderSummary,
} from "~/lib/tenders";

function primaryValue(tender: TenderSummary) {
	if (tender.awardedValue != null) {
		return { label: "Smluvní cena", value: tender.awardedValue };
	}
	return { label: "Předpokládaná hodnota", value: tender.estimatedValue };
}

export function TenderCard({ tender }: { tender: TenderSummary | CompanyTender }) {
	const value = primaryValue(tender);
	const companyTender = "myRoles" in tender ? tender : null;

	return (
		<article className="card-surface rounded-xl p-5 transition-shadow hover:shadow-md">
			<div className="flex flex-col gap-4">
				<div className="flex flex-wrap items-center gap-2">
					<span className="badge badge-soft badge-primary badge-sm">
						{TENDER_SOURCE_LABELS[tender.source] ?? tender.source.toUpperCase()}
					</span>
					{tender.sourceCount > 1 && (
						<span className="badge badge-soft badge-sm gap-1">
							<Layers3 className="h-3 w-3" /> {tender.sourceCount}{" "}
							{sourceRecordCountLabel(tender.sourceCount)}
						</span>
					)}
					{companyTender?.myRoles.map((role) => (
						<span
							key={role}
							className={`badge badge-soft badge-sm ${role === "winner" ? "badge-success" : "badge-ghost"}`}
						>
							{TENDER_ROLE_LABELS[role] ?? role}
						</span>
					))}
					{tender.hasBidPrices && (
						<span className="badge badge-soft badge-success badge-sm">Nabídkové ceny</span>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<Link
						to={`/verejna-zakazka/${tender.id}`}
						prefetch="intent"
						className="text-base font-semibold leading-snug hover:text-primary"
					>
						{tender.title || "Veřejná zakázka bez zveřejněného názvu"}
					</Link>
					{tender.buyerName && (
						<p className="flex items-center gap-1.5 text-sm text-muted-foreground">
							<Building2 className="h-3.5 w-3.5 shrink-0" />
							<span className="truncate">{tender.buyerName}</span>
							{tender.buyerIco && <span className="font-mono text-xs">IČO {tender.buyerIco}</span>}
						</p>
					)}
				</div>

				<div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
					<div>
						<p className="text-xs text-muted-foreground">{value.label}</p>
						<p className="font-mono font-semibold tabular-nums">
							{formatTenderAmount(value.value, tender.currency)}
						</p>
					</div>
					<div>
						<p className="text-xs text-muted-foreground">Zveřejněno</p>
						<p className="flex items-center gap-1 font-medium">
							<CalendarDays className="h-3.5 w-3.5" /> {formatDate(tender.publicationDate)}
						</p>
					</div>
					<div>
						<p className="text-xs text-muted-foreground">Kategorie CPV</p>
						<p
							className="truncate font-medium"
							title={tender.cpvLabel ?? tender.mainCpv ?? undefined}
						>
							{tender.cpvLabel ?? tender.mainCpv ?? "–"}
						</p>
					</div>
					<div>
						<p className="text-xs text-muted-foreground">Nabídky</p>
						<p className="flex items-center gap-1 font-medium">
							{tender.bidCount != null ? (
								<>
									<Users className="h-3.5 w-3.5" /> {tender.bidCount}
								</>
							) : (
								<>
									<Gavel className="h-3.5 w-3.5" /> neuvedeno
								</>
							)}
						</p>
					</div>
				</div>
			</div>
		</article>
	);
}
